import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdLocationOn, MdSearch } from 'react-icons/md';
import { observer } from 'mobx-react';

const NOMINATIM = 'https://nominatim.openstreetmap.org/search';

const QrLocationSearch = observer(({ store }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeIdx, setActiveIdx] = useState(-1);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // colours
  const wrapperBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const inputBg    = useColorModeValue('white', 'gray.700');
  const dropBg     = useColorModeValue('white', 'gray.750');
  const dropBorder = useColorModeValue('gray.200', 'gray.600');
  const rowHover   = useColorModeValue('blue.50', 'whiteAlpha.100');
  const rowActive  = useColorModeValue('blue.100', 'whiteAlpha.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const successBg  = useColorModeValue('green.50', 'green.900');
  const successBorder = useColorModeValue('green.200', 'green.700');
  const successText = useColorModeValue('green.700', 'green.200');

  // close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `${NOMINATIM}?q=${encodeURIComponent(q)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      setSuggestions(data);
      setOpen(data.length > 0);
      setActiveIdx(-1);
    } catch {
      setSuggestions([]);
    }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setSuggestions([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const selectSuggestion = (item) => {
    const lat = parseFloat(item.lat).toFixed(6);
    const lng = parseFloat(item.lon).toFixed(6);
    store.setFormData('lat', lat);
    store.setFormData('lng', lng);
    setSelected({ lat, lng, label: item.display_name });
    setQuery(item.display_name);
    setSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const shortLabel = (item) => {
    const parts = item.display_name.split(', ');
    // show first 2–3 parts as title, rest as subtitle
    const title = parts.slice(0, 2).join(', ');
    const sub = parts.slice(2, 5).join(', ');
    return { title, sub };
  };

  return (
    <Box mt={3} p={4} borderRadius="lg" bg={wrapperBg} display="flex" flexDirection="column" gap={4}>

      {/* Autocomplete search */}
      <Box ref={containerRef} position="relative">
        <Text fontSize="xs" fontWeight="medium" color={labelColor} mb={1.5}>
          Search place
        </Text>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none" color="gray.400">
            {loading ? <Spinner size="xs" /> : <MdSearch size={16} />}
          </InputLeftElement>
          <Input
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            placeholder="Eiffel Tower, Times Square…"
            borderRadius="lg"
            bg={inputBg}
            pl="32px"
            autoComplete="off"
          />
        </InputGroup>

        {/* Dropdown */}
        {open && suggestions.length > 0 && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            right={0}
            zIndex={50}
            bg={dropBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={dropBorder}
            boxShadow="lg"
            overflow="hidden"
          >
            {suggestions.map((item, idx) => {
              const { title, sub } = shortLabel(item);
              const isActive = idx === activeIdx;
              return (
                <Box
                  key={item.place_id}
                  px={3}
                  py={2.5}
                  cursor="pointer"
                  bg={isActive ? rowActive : undefined}
                  _hover={{ bg: rowHover }}
                  borderBottomWidth={idx < suggestions.length - 1 ? '1px' : '0'}
                  borderColor={dropBorder}
                  onMouseDown={() => selectSuggestion(item)}
                  display="flex"
                  gap={2.5}
                  alignItems="flex-start"
                >
                  <Box pt="1px" color="blue.400" flexShrink={0}>
                    <MdLocationOn size={14} />
                  </Box>
                  <Box minW={0}>
                    <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                      {title}
                    </Text>
                    {sub && (
                      <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                        {sub}
                      </Text>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Selected result badge */}
      {selected && (
        <Box
          p={2.5}
          borderRadius="lg"
          bg={successBg}
          borderWidth="1px"
          borderColor={successBorder}
        >
          <Text fontSize="xs" fontWeight="semibold" color={successText} mb={0.5}>
            ✓ Location set
          </Text>
          <Text fontSize="xs" color={mutedColor} noOfLines={2} lineHeight="1.4">
            {selected.label}
          </Text>
          <Text fontSize="xs" color={mutedColor} mt={1} fontFamily="mono">
            {selected.lat}, {selected.lng}
          </Text>
        </Box>
      )}

      {/* Manual coordinates fallback */}
      <Box>
        <Text fontSize="xs" color={mutedColor} mb={2}>Or enter coordinates manually</Text>
        <Box display="flex" gap={2}>
          <FormControl flex="1">
            <FormLabel fontSize="xs" mb={1} color={labelColor}>Latitude</FormLabel>
            <Input
              size="sm"
              type="number"
              value={store.formData.lat ?? ''}
              onChange={(e) => { store.setFormData('lat', e.target.value); setSelected(null); }}
              placeholder="40.7128"
              borderRadius="lg"
              bg={inputBg}
            />
          </FormControl>
          <FormControl flex="1">
            <FormLabel fontSize="xs" mb={1} color={labelColor}>Longitude</FormLabel>
            <Input
              size="sm"
              type="number"
              value={store.formData.lng ?? ''}
              onChange={(e) => { store.setFormData('lng', e.target.value); setSelected(null); }}
              placeholder="-74.0060"
              borderRadius="lg"
              bg={inputBg}
            />
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
});

export default QrLocationSearch;
